import { useLazyQuery } from '@apollo/client';
import React, { FC, useEffect, useState } from 'react';
import { ChildContainerStyled } from '../styled';
import { ThreeWeekLookAheadHeader } from './header';
import { ThreeWLAWeekHeader } from './week-header';
import { PersonaType, CrewMember } from '@bechtel/types';
import { GET_THREE_WEEK_LOOK_AHEAD_DATA } from '@bechtel/client-gql';
import { WeeklyLookAheadStatsByPersona, CrewMemberWeeklyLookAheadStatsInput } from '@bechtel/types';
import { CrewTree } from './crew-tree';
import { UNIT_OF_MEASURE } from './types';
import { Box } from '@bechtel/gds';
import { usePlanning, useAppUser, AllSuperintendentId } from '@bechtel/client-common';
import { Trans } from '@lingui/macro';

interface ThreeWeekLookAheadProps {
  selectedCrewMember: CrewMember | null;
}
export const ThreeWeekLookAhead: FC<ThreeWeekLookAheadProps> = ({ selectedCrewMember }) => {
  const { selectedSuperintendent, selectedGeneralForeman } = usePlanning();
  const [unitOfMeasure, setUnitOfMeasure] = useState<UNIT_OF_MEASURE>(UNIT_OF_MEASURE.HOUR);
  const [getWeeklyLookAheadStatsByPersona, result] = useLazyQuery<
    { weeklyLookAheadStatsByPersona: WeeklyLookAheadStatsByPersona[] },
    { input: CrewMemberWeeklyLookAheadStatsInput }
  >(GET_THREE_WEEK_LOOK_AHEAD_DATA, { fetchPolicy: 'network-only' });
  const { user } = useAppUser();

  useEffect(() => {
    if (!selectedSuperintendent) return;
    const input: CrewMemberWeeklyLookAheadStatsInput = {
      numNextWeeks: 2,
      numPreviousWeeks: 1,
      isAllSuperintendents: false,
    };
    if (selectedSuperintendent.id === AllSuperintendentId) {
      input.isAllSuperintendents = true;
    } else {
      input.crewMember = {
        persona: PersonaType.SUPERINTENDENT,
        crewMemberId: selectedSuperintendent.id,
      };
    }
    getWeeklyLookAheadStatsByPersona({
      variables: {
        input: input,
      },
    });
  }, [selectedSuperintendent, getWeeklyLookAheadStatsByPersona]);

  return (
    <ChildContainerStyled style={{ height: 'max-content' }}>
      <ThreeWeekLookAheadHeader
        unitOfMeasure={unitOfMeasure}
        setUnitOfMeasure={setUnitOfMeasure}
        disabled={!selectedCrewMember}
      />
      <ThreeWLAWeekHeader />
      {(!selectedCrewMember || !selectedCrewMember.persona) && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          py={{ sm: '900' }}
          flexGrow={{ sm: 1 }}
          data-testid="no-persona-selected"
        >
          <Trans>No user or persona selected</Trans>
        </Box>
      )}
      {selectedCrewMember && (
        <CrewTree
          result={result}
          unitOfMeasure={unitOfMeasure}
          selectedCrewMember={selectedCrewMember}
          selectedSuperintendent={selectedSuperintendent}
          selectedGeneralForeman={selectedGeneralForeman}
          depth={0}
          user={user}
        />
      )}
    </ChildContainerStyled>
  );
};
